import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { ref, uploadBytes } from 'firebase/storage';
import { UploadResult } from 'firebase/storage';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';

import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

import { ActionProps } from '../../contextProviders/AppProvider';
import { useData } from '../../contextProviders/DataProvider';
import { storage } from '../../firebase';
import { useAutoSave } from '../../stateHooks/useAutoSave';
import GratiRichTextContent from '../../types/GratiRichTextContent';
import { DOC_CONTENT_TYPES } from '../../types/GratiRichTextContent';
import Handbook, { HANDBOOK_STATUSES } from '../../types/Handbook';
import GratiImageUpload from '../common/GratiImageUpload';
import GratiRichEditor from '../common/GratiRichEditor';
import GratiSection, { asAdminOnly, type GratiSectionAction } from '../common/GratiSection';
import GratiSystemError from '../common/GratiSystemError';
import OrgSelector from '../org/OrgSelector';

export type HandbookEditorProps = {
  handbook: Handbook;
  isNew?: boolean;
  initialSection?: string;
  handleCreate?: (handbook: Handbook) => void;
  handleDelete?: (handbook: Handbook) => void;
  updateHandbook?: (handbook: Partial<Handbook>) => Promise<void>;
  onActionsChange?: (pageActions: ActionProps[] | null) => void;
};

export default function HandbookEditor(props: HandbookEditorProps): ReactElement {
  const {
    isNew = false,
    initialSection = '0',
    handleCreate,
    handleDelete,
    updateHandbook,
    onActionsChange,
  } = props;

  const { canCreateHandbooks, isOrgAdmin, getAdminOrgs } = useData();

  const [section, setSection] = useState(initialSection);
  const [deleteHandbookConfirm, setDeleteHandbookConfirm] = useState(false);
  const [deleteSectionConfirm, setDeleteSectionConfirm] = useState('');
  const [handbook, setHandbook] = useState<Handbook>(props.handbook);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);

  const hasMultipleAdminOrgs = getAdminOrgs().length > 1;

  const { queueChange } = useAutoSave<Handbook>({
    updateFn: updateHandbook,
    itemId: handbook.itemId,
    additionalFields: { orgId: handbook.orgId },
    enabled: !isNew && !!updateHandbook,
  });

  const handleFieldChange = <K extends keyof Handbook>(field: K, value: Handbook[K]) => {
    setHandbook((prev) => ({ ...prev, [field]: value }));
    queueChange(field, value);
  };

  const handleStatusChange = useCallback((newStatus: string) => {
    setHandbook((prev) => ({ ...prev, status: newStatus as Handbook['status'] }));
    if (!isNew) {
      queueChange('status', newStatus as Handbook['status']);
    }
  }, [isNew, queueChange]);

  const handleDeleteHandbook = () => {
    if (handleDelete) {
      handleDelete(handbook);
    }
    setDeleteHandbookConfirm(false);
  };

  const handleDeleteSection = () => {
    const newSections = [...handbook.sections];
    const deletedIndex = parseInt(deleteSectionConfirm);
    newSections.splice(deletedIndex, 1);
    setHandbook((prev) => ({ ...prev, sections: newSections }));
    queueChange('sections', newSections);
    setDeleteSectionConfirm('');
    const newIndex = Math.min(deletedIndex, newSections.length - 1);
    if (newIndex >= 0) {
      setSection(newIndex.toString());
    } else {
      setSection('0');
    }
  };

  const handleAddSection = () => {
    const newSections = [...handbook.sections];
    newSections.push({ title: 'New Section', html: '<p></p>', type: DOC_CONTENT_TYPES.CHAPTER });
    setHandbook((prev) => ({ ...prev, sections: newSections }));
    queueChange('sections', newSections);
    setSection((newSections.length - 1).toString());
  };

  const handleImageUpdated = (snapshot: UploadResult | null) => {
    if (handbook) {
      const logo = snapshot ? snapshot.ref.name : '';
      setHandbook((prev) => ({ ...prev, logo }));
      if (!isNew && updateHandbook) {
        updateHandbook({ itemId: handbook.itemId, logo });
      }
    }
  };

  const handleFileSelected = (file: File | null) => {
    setPendingLogoFile(file);
  };

  const onSubmit = async () => {
    if (!handleCreate) return;
    try {
      let handbookToSave = handbook;
      if (pendingLogoFile && isNew) {
        const logoRef = ref(
          storage,
          `org/${handbookToSave.orgId}/handbook/${handbookToSave.itemId}/orgMemberAdminFile/${handbookToSave.itemId}-logo`);
        const snapshot = await uploadBytes(logoRef, pendingLogoFile);
        handbookToSave = { ...handbookToSave, logo: snapshot.ref.name };
      }
      handleCreate(handbookToSave);
    } catch (error) {
      console.error('Error saving handbook:', error);
    }
  };

  const handleSectionChange = useCallback((sectionData: GratiRichTextContent, index: number) => {
    setHandbook((prev) => {
      const newSections = [...prev.sections];
      newSections[index] = sectionData;
      queueChange('sections', newSections);
      return { ...prev, sections: newSections };
    });
  }, [queueChange]);

  const onActionsChangeRef = useRef(onActionsChange);
  useEffect(() => {
    onActionsChangeRef.current = onActionsChange;
  });

  const handbookName = handbook.name;
  const handbookOrgId = handbook.orgId;

  // Page action bar: Create button for new handbooks only
  useEffect(() => {
    if (!onActionsChangeRef.current) return;
    const pageActions: ActionProps[] | null = isNew ? [
      { name: 'Create', icon: CheckIcon, type: 'submit', form: 'handbook-editor-form', disabled: !handbookName.trim() || !handbookOrgId, variant: 'contained', color: 'primary' },
    ] : null;
    onActionsChangeRef.current(pageActions);
  }, [handbookName, handbookOrgId, isNew]);

  const handbookStatus = handbook.status;
  const isArchived = handbookStatus === HANDBOOK_STATUSES.ARCHIVED;

  // Draft / Publish as in every section; an archived handbook is neither, so it says so itself
  const editorSecondaryActions: GratiSectionAction[] = isArchived
    ? [
        { text: 'Publish', onClick: () => handleStatusChange(HANDBOOK_STATUSES.ACTIVE) },
        { text: 'Restore to Draft', onClick: () => handleStatusChange(HANDBOOK_STATUSES.DRAFT) },
      ]
    : [{ text: 'Archive', onClick: () => handleStatusChange(HANDBOOK_STATUSES.ARCHIVED) }];

  const isAuthorized = (isNew && canCreateHandbooks()) || (handbook.orgId && isOrgAdmin(handbook.orgId));

  if (handbook) {
    if (isAuthorized) {
      return (
        <Box
          id="handbook-editor-form"
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <Stack spacing={2}>
            <GratiSection
              headerStyle="filled"
              title={handbook.name || (isNew ? 'New Handbook' : '')}
              isEditing={!isNew}
              secondaryActions={!isNew ? asAdminOnly(editorSecondaryActions) : undefined}
              publish={!isArchived ? {
                isPublished: handbookStatus === HANDBOOK_STATUSES.ACTIVE,
                onPublish: () => handleStatusChange(HANDBOOK_STATUSES.ACTIVE),
                onUnpublish: !isNew ? () => handleStatusChange(HANDBOOK_STATUSES.DRAFT) : undefined,
              } : undefined}
              color={isArchived ? 'var(--mui-palette-text-disabled)' : undefined}
              adminOnly={isArchived ? { status: 'Archived' } : undefined}
            />
            {isNew && hasMultipleAdminOrgs ? (
              <OrgSelector
                value={handbook.orgId}
                onChange={(orgPair) => setHandbook({ ...handbook, orgId: orgPair.orgId, orgName: orgPair.orgName })}
              />
            ) : (
              <TextField
                id="org"
                label="Org"
                value={handbook.orgName}
                disabled
              />
            )}
            <TextField
              id="name"
              label="Title"
              aria-label="Handbook Name"
              aria-required="true"
              required
              value={handbook.name}
              onChange={({ target }) => handleFieldChange('name', target.value)}
            />
            <GratiImageUpload
              onImageUpdated={isNew ? undefined : handleImageUpdated}
              onFileSelected={isNew ? handleFileSelected : undefined}
              deferUpload={isNew}
              isInitialized={!isNew && Boolean(handbook.logo)}
              storageImageFileName={handbook.itemId + '-logo'}
              storageImageLocation={`org/${handbook.orgId}/handbook/${handbook.itemId}/orgMemberAdminFile`}
              label="Icon"
            />
            <TabContext value={section}>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                  width: "100%",
                  border: 1,
                  borderColor: 'outline.variant',
                  borderRadius: 1
                }}>
                <TabList
                  onChange={(_event, newValue) => setSection(newValue)}
                  variant="fullWidth"
                  scrollButtons="auto"
                  aria-label="handbook content tabs"
                  slotProps={{
                    indicator: {
                      sx: {
                        color: 'primary.onContainer',
                      },
                    },
                  }}
                >
                  {handbook.sections.map((sectionItem, index) => (
                    <Tab
                      key={index}
                      value={index.toString()}
                      label={sectionItem.title || `Section ${index + 1}`}
                      wrapped
                      sx={{
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.container',
                          color: 'primary.onContainer',
                        },
                        '&:not(.Mui-selected)': {
                          color: 'primary.onContainerVariant',
                        },
                      }}
                    />
                  ))}
                </TabList>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={handleAddSection}
                >
                  Add section
                </Button>
              </Stack>
              {handbook.sections.map((sectionItem, index) => (
                <TabPanel key={index} value={index.toString()}>
                  <Stack direction="column" spacing={2}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      sx={{
                        justifyContent: { xs: 'center', sm: 'flex-start' },
                        alignItems: { xs: 'flex-start', sm: 'center' }
                      }}>
                      <TextField
                        id={'sectionTitle' + index}
                        label="Section Title"
                        aria-label="Section Title"
                        aria-required="true"
                        value={sectionItem.title}
                        onChange={(e) => {
                          handleSectionChange({ ...sectionItem, title: e.target.value }, index);
                        }}
                      />
                      <Button
                        size="small"
                        variant="text"
                        color="secondary"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteSectionConfirm(index.toString())}
                      >
                        Delete section
                      </Button>
                      {deleteSectionConfirm === index.toString() && (
                        <span>
                          <Button size="large" onClick={handleDeleteSection}>
                            Confirm
                          </Button>
                          <Button size="large" onClick={() => setDeleteSectionConfirm('')}>
                            Cancel
                          </Button>
                        </span>
                      )}
                    </Stack>
                    <GratiRichEditor
                      section={sectionItem}
                      sectionIndex={index}
                      onChange={handleSectionChange}
                    />
                  </Stack>
                </TabPanel>
              ))}
            </TabContext>

            {!isNew && handleDelete && (
              <Stack direction="row" spacing={2} sx={{
                alignItems: "center"
              }}>
                {!deleteHandbookConfirm && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteHandbookConfirm(true)}
                  >
                    Delete
                  </Button>
                )}
                {deleteHandbookConfirm && (
                  <>
                    <Button size="small" color="error" onClick={handleDeleteHandbook}>
                      Confirm Delete
                    </Button>
                    <Button size="small" onClick={() => setDeleteHandbookConfirm(false)}>
                      Cancel
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      );
    } else {
      return <GratiSystemError error={new Error('Not authorized to edit this handbook')} />;
    }
  } else {
    return <></>;
  }
}
